/**
 * Color tokens for remediation request statuses, urgency, and severity.
 * Status codes match the Remediation Hub API (17 values).
 *
 * Each status defines chip bg/fg/border plus an optional rowBg for list rows.
 */

export const REMEDIATION_STATUSES = [
  "REQUESTED",
  "ASSUMING_ROLE",
  "CHECKING_PERMS",
  "PRECHECK_RUNNING",
  "PLAN_BUILDING",
  "NEEDS_INPUTS",
  "DRY_RUN_READY",
  "APPROVED",
  "IN_PROGRESS",
  "COMPLETED",
  "FAILED",
  "CANCELLED",
  "ALREADY_REMEDIATED",
  "PERMS_DENIED",
  "PRECHECK_FAILED",
  "VERIFY_FAILED",
  "ABANDONED",
];

export const STATUS_ROW_GROUP = {
  ACTIVE: "active",
  ATTENTION: "attention",
  SUCCESS: "success",
  FAILURE: "failure",
  INACTIVE: "inactive",
};

const INACTIVE_STATUSES = new Set(["CANCELLED", "ABANDONED"]);

export const STATUS_META = {
  REQUESTED: {
    label: "Validation",
    group: STATUS_ROW_GROUP.ACTIVE,
    bg: "#e0e7ff",
    fg: "#3730a3",
    border: "#a5b4fc",
    rowBg: "#ffffff",
    urgency: 25,
    description: "CloudCatcher is checking that the fix can be applied safely.",
  },
  ASSUMING_ROLE: {
    label: "Validation",
    group: STATUS_ROW_GROUP.ACTIVE,
    bg: "#dbeafe",
    fg: "#1e40af",
    border: "#93c5fd",
    rowBg: "#ffffff",
    urgency: 28,
    description: "CloudCatcher is checking that the fix can be applied safely.",
  },
  CHECKING_PERMS: {
    label: "Validation",
    group: STATUS_ROW_GROUP.ACTIVE,
    bg: "#dbeafe",
    fg: "#1d4ed8",
    border: "#93c5fd",
    rowBg: "#ffffff",
    urgency: 32,
    description: "CloudCatcher is checking that the fix can be applied safely.",
  },
  PRECHECK_RUNNING: {
    label: "Validation",
    group: STATUS_ROW_GROUP.ACTIVE,
    bg: "#ede9fe",
    fg: "#5b21b6",
    border: "#c4b5fd",
    rowBg: "#ffffff",
    urgency: 60,
    description: "CloudCatcher is checking that the fix can be applied safely.",
  },
  PLAN_BUILDING: {
    label: "Validation",
    group: STATUS_ROW_GROUP.ACTIVE,
    bg: "#ede9fe",
    fg: "#6d28d9",
    border: "#c4b5fd",
    rowBg: "#ffffff",
    urgency: 55,
    description: "CloudCatcher is checking that the fix can be applied safely.",
  },
  NEEDS_INPUTS: {
    label: "Awaiting Approval",
    group: STATUS_ROW_GROUP.ATTENTION,
    bg: "#fee2e2",
    fg: "#b91c1c",
    border: "#fca5a5",
    rowBg: "#fff5f5",
    urgency: 100,
    description: "The validation plan is ready; supply any required inputs, then approve or cancel.",
  },
  DRY_RUN_READY: {
    label: "Awaiting Approval",
    group: STATUS_ROW_GROUP.ATTENTION,
    bg: "#ffedd5",
    fg: "#c2410c",
    border: "#fdba74",
    rowBg: "#fff7ed",
    urgency: 80,
    description: "The validation plan is ready; we need your OK to proceed.",
  },
  APPROVED: {
    label: "Scheduled",
    group: STATUS_ROW_GROUP.ACTIVE,
    bg: "#d1fae5",
    fg: "#047857",
    border: "#6ee7b7",
    rowBg: "#ffffff",
    urgency: 50,
    description: "Approved and queued to run at the chosen time.",
  },
  IN_PROGRESS: {
    label: "In Progress",
    group: STATUS_ROW_GROUP.ACTIVE,
    bg: "#bfdbfe",
    fg: "#1e3a8a",
    border: "#60a5fa",
    rowBg: "#ffffff",
    urgency: 40,
    description: "Actively applying the fix against your AWS account.",
  },
  COMPLETED: {
    label: "Completed",
    group: STATUS_ROW_GROUP.SUCCESS,
    bg: "#bbf7d0",
    fg: "#15803d",
    border: "#86efac",
    rowBg: "#f0fdf4",
    urgency: 0,
    description: "Every step of the fix ran successfully and post-run verification passed.",
  },
  FAILED: {
    label: "Failed",
    group: STATUS_ROW_GROUP.FAILURE,
    bg: "#fbcfe8",
    fg: "#be185d",
    border: "#f9a8d4",
    rowBg: "#fdf2f8",
    urgency: 0,
    description: "CloudCatcher started applying the fix but at least one AWS API call did not succeed.",
  },
  CANCELLED: {
    label: "Cancelled",
    group: STATUS_ROW_GROUP.INACTIVE,
    outlined: true,
    bg: "transparent",
    fg: "#475569",
    border: "#94a3b8",
    rowBg: "#e2e8f0",
    urgency: 0,
    description: "Cancelled before the run started. File a fresh task to reapply.",
  },
  ALREADY_REMEDIATED: {
    label: "Already Remediated",
    group: STATUS_ROW_GROUP.SUCCESS,
    bg: "#e2e8f0",
    fg: "#475569",
    border: "#cbd5e1",
    rowBg: "#f1f5f9",
    urgency: 0,
    description: "The resource was already in the desired state. No changes were made.",
  },
  PERMS_DENIED: {
    label: "Permission Denied",
    group: STATUS_ROW_GROUP.ATTENTION,
    bg: "#fee2e2",
    fg: "#991b1b",
    border: "#fca5a5",
    rowBg: "#fff1f2",
    urgency: 90,
    description: "The CloudCatcher trust role is missing IAM actions this fix needs. The task retries automatically after you update the IAM stack.",
  },
  PRECHECK_FAILED: {
    label: "Failed",
    group: STATUS_ROW_GROUP.FAILURE,
    bg: "#fde68a",
    fg: "#92400e",
    border: "#fcd34d",
    rowBg: "#fffbeb",
    urgency: 70,
    description: "Validation could not complete. See the task timeline for the AWS error and next steps.",
  },
  VERIFY_FAILED: {
    label: "Failed",
    group: STATUS_ROW_GROUP.FAILURE,
    bg: "#fde68a",
    fg: "#78350f",
    border: "#fcd34d",
    rowBg: "#fffbeb",
    urgency: 72,
    description: "The fix ran but post-run verification did not confirm the expected state.",
  },
  ABANDONED: {
    label: "Abandoned",
    group: STATUS_ROW_GROUP.INACTIVE,
    outlined: true,
    bg: "transparent",
    fg: "#64748b",
    border: "#94a3b8",
    rowBg: "#e2e8f0",
    urgency: 0,
    description: "Sat in a customer-wait state for more than one year without being approved. File a fresh task to try again.",
  },
};

export const SEVERITY_META = {
  CRITICAL: { bg: "#fef2f2", fg: "#b91c1c", border: "#fecaca" },
  HIGH: { bg: "#fff1f2", fg: "#e11d48", border: "#fecdd3" },
  MEDIUM: { bg: "#fffbeb", fg: "#d97706", border: "#fde68a" },
  LOW: { bg: "#ecfdf5", fg: "#059669", border: "#a7f3d0" },
  INFORMATIONAL: { bg: "#eff6ff", fg: "#2563eb", border: "#bfdbfe" },
};

export const TERMINAL_STATUSES = new Set([
  "COMPLETED",
  "FAILED",
  "CANCELLED",
  "ALREADY_REMEDIATED",
  "ABANDONED",
]);

export const ATTENTION_NEEDED_STATUSES = [
  "NEEDS_INPUTS",
  "PERMS_DENIED",
  "DRY_RUN_READY",
  "PRECHECK_FAILED",
  "VERIFY_FAILED",
];

export const ACTIVE_STATUSES = [
  "REQUESTED",
  "ASSUMING_ROLE",
  "CHECKING_PERMS",
  "PRECHECK_RUNNING",
  "PLAN_BUILDING",
  "APPROVED",
  "IN_PROGRESS",
];

/** Statuses where cancel / re-schedule actions are disabled. */
export const NON_ACTIONABLE_STATUSES = new Set([
  ...TERMINAL_STATUSES,
  "IN_PROGRESS",
]);

export const IN_FLIGHT_STATUSES = new Set([
  "REQUESTED",
  "ASSUMING_ROLE",
  "CHECKING_PERMS",
  "PRECHECK_RUNNING",
  "PLAN_BUILDING",
  "IN_PROGRESS",
]);

/** All API statuses, highest urgency first (for filter checklist). */
export const ALL_STATUSES = [...REMEDIATION_STATUSES].sort(
  (a, b) => (STATUS_META[b]?.urgency ?? 0) - (STATUS_META[a]?.urgency ?? 0)
);

/**
 * Customer KB statuses. The API still uses the codes in `statuses`;
 * the UI shows `label` on chips, filters, and the list.
 */
export const CUSTOMER_STATUS_FILTERS = [
  {
    id: "validation",
    label: "Validation",
    statuses: ["REQUESTED", "ASSUMING_ROLE", "CHECKING_PERMS", "PRECHECK_RUNNING", "PLAN_BUILDING"],
  },
  {
    id: "awaiting_approval",
    label: "Awaiting Approval",
    statuses: ["DRY_RUN_READY", "NEEDS_INPUTS"],
  },
  { id: "scheduled", label: "Scheduled", statuses: ["APPROVED"] },
  { id: "in_progress", label: "In Progress", statuses: ["IN_PROGRESS"] },
  { id: "completed", label: "Completed", statuses: ["COMPLETED"] },
  {
    id: "failed",
    label: "Failed",
    statuses: ["FAILED", "PRECHECK_FAILED", "VERIFY_FAILED"],
  },
  { id: "permission_denied", label: "Permission Denied", statuses: ["PERMS_DENIED"] },
  { id: "already_remediated", label: "Already Remediated", statuses: ["ALREADY_REMEDIATED"] },
  { id: "cancelled", label: "Cancelled", statuses: ["CANCELLED"] },
  { id: "abandoned", label: "Abandoned", statuses: ["ABANDONED"] },
];

export function getCustomerStatusFilter(status) {
  return CUSTOMER_STATUS_FILTERS.find((g) => g.statuses.includes(status)) || null;
}

export function getStatusLabel(status) {
  if (!status) return "Unknown";
  return STATUS_META[status]?.label || String(status).replace(/_/g, " ");
}

export function getStatusMeta(status) {
  return (
    STATUS_META[status] || {
      label: status || "Unknown",
      group: STATUS_ROW_GROUP.ACTIVE,
      bg: "#f1f5f9",
      fg: "#64748b",
      border: "#e2e8f0",
      rowBg: "#ffffff",
      description: "Unknown status.",
    }
  );
}

export function getStatusRowGroup(status) {
  return getStatusMeta(status).group || STATUS_ROW_GROUP.ACTIVE;
}

export function isInactiveStatus(status) {
  return INACTIVE_STATUSES.has(status);
}
